import { WebVTTThumbnail } from "./Webvtt.js";
import { ThumbnailPositionOnSeekbarHover, ThumbnailPositionOnElementHoverViaTimestamp } from "./ThumbnailGalleryCover.js"

export { Thumbnail }

/**
 * Define methods for the thumbnail preview in the video player when hovering over its seek bar
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
class Thumbnail {
    /**
     * Extend the functionality of the video player seek bar by showing thumbnail images for mouse hovering
     * @param {Object} targetElement - video player container element
     * @param {string} thumbnailPath - path to the webVTT thumbnail file
     * @param {Number} preloadThumbnailsNumber - number of thumbnails that should be preloaded
     * @param {boolean} preloadThumbnailsSparse - if true, load the thumbnails spread over the whole video, otherwise ascending from the beginning (for separated thumbnail images only)
     * @param {string} seekbarSelector - selector for the seekbar relative to the target element
     * @param {Object} timeElementSelector - selector of time element that contains the timestamp as innerText
     */
    constructor(targetElement, thumbnailPath, preloadThumbnailsNumber, preloadThumbnailsSparse, seekbarSelector, timeElementSelector = undefined) {
        this.videoContainerElement = targetElement;
        this.timeElementSelector = timeElementSelector;
        this.thumbnailPath = thumbnailPath;
        this.videoElement = this.videoContainerElement.querySelector('video')
        this.seekbarSelector = seekbarSelector;
        const thumbnailObject = new WebVTTThumbnail(this.thumbnailPath);
        thumbnailObject.init(preloadThumbnailsNumber, preloadThumbnailsSparse);
        this.buildThumbnailBox_();
        this.computeThumbnailBoxStyle_();
        this.setThumbnailBoxStyle_();
        this.initThumbnailPainting_();

        this.seekbar.addEventListener('pointermove', (event) => {
            this.thumbnailContainerElement.style.display = "block";
            this.computeThumbnailBoxStyle_();
            this.setThumbnailBoxStyle_();


            // Note: observe that event.target might not be this.seekbar 
            const seekbarCoordinateX = this.seekbar.getBoundingClientRect().left;
            const pointerCoordinateX = event.clientX;
            const pointerOffsetX = pointerCoordinateX - seekbarCoordinateX;

            const seekbarWidth = this.seekbar.getBoundingClientRect().width;

            // optional: if seekbar has padding, include padding removal here
            // const paddingLeft = 10;
            // const paddingRight = 10;

            const offsetLeft = (this.videoElementWidth - this.thumbnailBoxWidth) * pointerOffsetX / seekbarWidth;

            const offsetLeftTouchscreenLimit = Math.min(Math.max(0, offsetLeft), this.videoElementWidth - this.thumbnailBoxWidth);
            this.thumbnailContainerElement.style.left = offsetLeftTouchscreenLimit + "px";
        }, true)
        this.seekbar.addEventListener('pointerleave', (event) => { this.thumbnailContainerElement.style.display = "none" })
    }

    /**
     * Init thumbnail class based on mode: via timestamp or via seekbar
     * @private
     */
    initThumbnailPainting_() {
        if (this.timeElementSelector) {
            new ThumbnailPositionOnElementHoverViaTimestamp(this.seekbar, this.thumbnailCanvasElement, this.timeElementSelector, this.thumbnailBoxWidth, this.thumbnailBoxHeight, this.thumbnailPath, this.timestampTextElement, "")
        }
        else {
            new ThumbnailPositionOnSeekbarHover(this.seekbar, this.thumbnailCanvasElement, this.thumbnailBoxWidth, this.thumbnailBoxHeight, this.thumbnailPath, this.timestampTextElement, "");
        }
    }

    /**
     * Build the thumbnail box containing a thumbnail media element for canvas and a time element for timestamps
     * @private
     */
    buildThumbnailBox_() {
        this.seekbar = this.videoContainerElement.querySelector(this.seekbarSelector);
        this.thumbnailContainerElement = document.createElement('div');
        this.thumbnailContainerElement.className = 'thumbnail-box';
        this.thumbnailContainerElement.style.display = "none";
        this.thumbnailCanvasElement = document.createElement('div');
        this.thumbnailCanvasElement.className = 'thumbnail-media';
        this.timestampTextElement = document.createElement('div');
        this.timestampTextElement.className = 'thumbnail-time';
        this.thumbnailContainerElement.append(this.timestampTextElement, this.thumbnailCanvasElement);
        this.videoElement.parentElement.append(this.thumbnailContainerElement);
    }

    /**
     * Compute the width and the height of the thumbnail box relative to the width and height of the video player. Furthermore, compute the bottom of the thumbnail box.
     * @private
     */
    computeThumbnailBoxStyle_() {
        const html5VideoHeight = this.videoElement.videoHeight;
        const html5VideoWidth = this.videoElement.videoWidth;
        if (!html5VideoHeight || !html5VideoWidth) {
            console.error(`Video element not fully loaded (detected width: ${html5VideoWidth}, height: ${html5VideoHeight})`)
        }
        const videoAspectRatio = html5VideoHeight / html5VideoWidth;
        this.videoElementWidth = this.videoElement.getBoundingClientRect().width;
        this.thumbnailBoxWidth = 0.30 * this.videoElementWidth > 250 ? 250 : 200;
        this.thumbnailBoxHeight = this.thumbnailBoxWidth * videoAspectRatio;
        this.thumbnailBoxBottom = this.videoElement.getBoundingClientRect().bottom - this.seekbar.getBoundingClientRect().top + 4;
    }

    /**
     * Set the width, height and bottom of the thumbnail box
     * @private
     */
    setThumbnailBoxStyle_() {
        this.thumbnailContainerElement.style.width = this.thumbnailBoxWidth + "px";
        this.thumbnailContainerElement.style.height = this.thumbnailBoxHeight + "px";
        this.thumbnailContainerElement.style.bottom = this.thumbnailBoxBottom + "px";
    }
}